"use client";

import React, { useRef } from "react";
import { motion } from "framer-motion";
import { WhatWeDoProps } from "@/app/components/landing/what-we-do/types/StoryChapterNew";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { GradientLine } from "@/app/components/ui/gradient-line";

const BackgroundGlow = () => (
  <>
    {/* Top-right glow */}
    <motion.div
      className="absolute top-0 right-0 w-[800px] h-[800px] bg-[rgb(var(--color-neon-blue)_/_0.4)] rounded-full blur-[120px] -translate-y-1/4"
      animate={{
        scale: [1, 1.1, 1],
        opacity: [0.4, 0.5, 0.4],
        x: [0, 20, 0],
        y: [-100, -80, -100],
      }}
      transition={{
        duration: 12,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />

    {/* Bottom-left glow */}
    <motion.div
      className="absolute bottom-0 left-0 w-[700px] h-[700px] bg-[rgb(var(--color-electric-blue)_/_0.4)] rounded-full blur-[120px] translate-y-1/4"
      animate={{
        scale: [1, 1.15, 1],
        opacity: [0.4, 0.5, 0.4],
        x: [0, -30, 0],
        y: [100, 80, 100],
      }}
      transition={{
        duration: 15,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />

    {/* Center glow */}
    <motion.div
      className="absolute top-1/2 right-1/4 w-[600px] h-[600px] bg-[rgb(var(--color-neon-green)_/_0.3)] rounded-full blur-[120px] -translate-y-1/2"
      animate={{
        scale: [1, 1.2, 1],
        opacity: [0.3, 0.4, 0.3],
        x: [0, 40, 0],
      }}
      transition={{
        duration: 18,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  </>
);

export const WhatWeDoIMG: React.FC<WhatWeDoProps> = ({ data, className }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={containerRef}
      id="what-we-do-content"
      className={cn(
        "w-full bg-[rgb(var(--color-primary-dark)_/_0.95)] text-white font-sans py-20 md:py-40 relative overflow-hidden",
        className
      )}
    >
      <BackgroundGlow />
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `linear-gradient(to right, rgb(255 255 255 / 0.02) 1px, transparent 1px),
                           linear-gradient(to bottom, rgb(255 255 255 / 0.02) 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[rgb(var(--color-primary-dark))] via-transparent to-[rgb(var(--color-primary-dark))]" />
      <div className="relative max-w-7xl mx-auto px-4 md:px-10 space-y-40">
        {data.map((chapter, index) => {
          const isEven = index % 2 === 0;
          return (
            <motion.div
              key={chapter.id}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5 }}
              className="flex flex-col md:flex-row items-center justify-between gap-10 md:gap-20"
            >
              {/* Text Content */}
              <motion.div
                className={cn(
                  "w-full md:w-1/2 space-y-6",
                  !isEven && "md:order-2"
                )}
                initial={{ x: isEven ? -50 : 50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                <div className="space-y-4">
                  <span className="inline-flex px-4 py-2 rounded-full text-sm font-medium bg-[rgb(var(--color-neon-blue)_/_0.1)] text-[rgb(var(--color-neon-blue-light))] border border-[rgb(var(--color-neon-blue)_/_0.2)]">
                    {chapter.description}
                  </span>
                  <h2 className="text-4xl md:text-6xl font-bold tracking-[-0.02em] text-white">
                    {chapter.title}
                  </h2>
                  <div className="text-[rgb(var(--color-text-secondary))] space-y-4 tracking-[0.01em] leading-[1.8]">
                    {chapter.longDescription
                      .split("\n")
                      .filter((text) => text.trim().length > 0)
                      .map((paragraph, index) => (
                        <p key={index} className="text-lg">
                          {paragraph.trim()}
                        </p>
                      ))}
                  </div>
                </div>
              </motion.div>

              {/* Image Container */}
              <motion.div
                className={cn(
                  "w-full md:w-1/2 relative",
                  !isEven && "md:order-1"
                )}
                initial={{ x: isEven ? 50 : -50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{
                  duration: 0.7,
                  delay: 0.3,
                  ease: [0.21, 0.47, 0.32, 0.98],
                }}
              >
                <div className="relative w-full h-full flex items-center justify-center">
                  <div className="relative w-fit rounded-lg overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-[rgb(var(--color-neon-blue)_/_0.1)] to-[rgb(var(--color-electric-blue)_/_0.1)] rounded-lg border border-[rgb(var(--color-neon-blue)_/_0.2)]" />
                    <Image
                      src={chapter.image}
                      alt={chapter.imageAlt}
                      width={500}
                      height={375}
                      className="object-contain"
                      priority={index === 0}
                      sizes="(max-width: 768px) 100vw, 50vw"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[rgb(var(--color-primary-dark)_/_0.3)] to-transparent" />
                  </div>
                </div>
              </motion.div>
            </motion.div>
          );
        })}
      </div>
      <div className="absolute bottom-0 left-0 w-2/3">
        <GradientLine />
      </div>
    </div>
  );
};
